import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material'
import ConsultasNoAsignHead from '../components/ConsultasNoAsignHead'
import ConsultasNoAsignTable from '../components/ConsultasNoAsignTable'
import CrearAsignacion from '../containers/CrearAsignacion'
import EditarClienteContainer from '../containers/EditarClienteContainer'
import ConfirmDialog from '../components/ConfirmDialog'
import ImportClientesCsvDialog from '../components/ImportClientesCsvDialog'
import socket from "../../socket"
import { apiFetch } from '../../api'
import { getUserContext } from '../../auth'


export default function ConsultasNoAsignadasPanel({ 
  onClose, 
  onOpenClosed, 
  onOpenClientes,
  isFullScreen, 
  onToggleFullScreen,
  highlightConsultaId = null,
  onHighlightConsumed,
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [clientes, setClientes] = useState([])
  const [total, setTotal] = useState(0) // Total de registros en BD
  const [page, setPage] = useState(0)   // Página actual (MUI usa base 0)
  const [limit, setLimit] = useState(20) // Filas por página

  const [selected, setSelected] = useState([])
  const [openAsignacion, setOpenAsignacion] = useState(false)
  const [sortOrder, setSortOrder] = useState('desc') // 'asc' | 'desc'
  const [importCsvOpen, setImportCsvOpen] = useState(false)
  const [activeHighlightId, setActiveHighlightId] = useState(null)
  
  // Flag para pausar refetches del socket durante operaciones masivas
  const pauseSocketRefetch = useRef(false)

  useEffect(() => {
    if (isFullScreen) {
      setLimit(100);
    } else {
      setLimit(20);
    }
    setPage(0);
  }, [isFullScreen]);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    cliente: null,
    multiple: false,
  })
  const [editDialog, setEditDialog] = useState({
    open: false,
    cliente: null,
  })

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  const fetchClientes = useCallback(async (mountedRef, orderKey = sortOrder) => {
    try {
      if (mountedRef && !mountedRef.current) return
      setLoading(true); setError(null)

      const params = new URLSearchParams()
      if (orderKey === 'asc') params.set('orden', '0')
      else if (orderKey === 'desc') params.set('orden', '1')

      // Agregar paginación (MUI page 0 -> API page 1)
      params.set('page', String(page + 1))
      params.set('limit', String(limit))

      const url = `/api/consultas_sin_asignacion${params.toString() ? `?${params}` : ''}`
      const res = await apiFetch(url, { credentials: 'include' })
      if (!res.ok) throw new Error('Error fetching consultas no asignadas')

      // La respuesta ahora es { items: [], total: ... }
      const data = await res.json()

      if (mountedRef && !mountedRef.current) return

      setClientes(Array.isArray(data.items) ? data.items : [])
      setTotal(data.total || 0)

    } catch (e) {
      if (mountedRef && !mountedRef.current) return
      setError(e?.message || 'Error fetching')
    } finally {
      if (mountedRef && !mountedRef.current) return
      setLoading(false)
    }
  }, [sortOrder, page, limit])

  const refetchTimerRef = useRef(null);

  const scheduleFetchClientes = useCallback((orderKey) => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
  
    refetchTimerRef.current = setTimeout(() => {
      const mountedRef = { current: true };
      fetchClientes(mountedRef, orderKey);
    }, 250);
  }, [fetchClientes]);


  useEffect(() => {
    const mountedRef = { current: true }
    fetchClientes(mountedRef)
    return () => { mountedRef.current = false }
  }, [fetchClientes])

  useEffect(() => {
    if (!highlightConsultaId) return;
    setActiveHighlightId(Number(highlightConsultaId));
    const timer = setTimeout(() => {
      setActiveHighlightId(null);
      onHighlightConsumed?.();
    }, 5000);
    return () => clearTimeout(timer);
  }, [highlightConsultaId, onHighlightConsumed]);


  // Socket listener para create_cliente
  useEffect(() => {
    const handleCreateCliente = (data) => {
      // Ignorar eventos del socket si estamos en operación masiva
      if (pauseSocketRefetch.current) return;

      // Filtrado por empresa
      const user = getUserContext();
      if (user && String(data?.empresa_id) !== String(user.empresa_id)) return;

      scheduleFetchClientes();
    };

    socket.on("create_cliente", handleCreateCliente);
    socket.on("update_cliente", handleCreateCliente);
    socket.on("delete_cliente", handleCreateCliente);
    return () => {
      socket.off("create_cliente", handleCreateCliente);
      socket.off("update_cliente", handleCreateCliente);
      socket.off("delete_cliente", handleCreateCliente);
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    };
  }, [scheduleFetchClientes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const toggleSelectAll = (checked) => {
    if (checked) setSelected(clientes.map(c => c.id))
    else setSelected([])
  }

  const toggleRow = (id, checked) => {
    if (checked) setSelected(prev => [...(prev || []), id])
    else setSelected(prev => (prev || []).filter(x => x !== id))
  }

  const handleOpenAsignacion = () => setOpenAsignacion(true)
  const handleCloseAsignacion = async () => {
    setOpenAsignacion(false)
    await fetchClientes()
    setSelected([])
  }

  const handleChangeSort = (val) => {
    setSortOrder(val)           // 'asc' | 'desc'
    fetchClientes(undefined, val)
  }

  // Función para obtener el cliente seleccionado cuando hay exactamente 1
  const getSelectedCliente = () => {
    if (selected.length === 1) {
      return clientes.find(c => c.id === selected[0]) || null
    }
    return null
  }

  // Función para editar cliente - ACTUALIZADA
  const handleEditCliente = (cliente) => {
    const clienteId = cliente?.id_cliente ?? cliente?.cliente_id ?? cliente?.id
    setEditDialog({
      open: true,
      cliente: { ...cliente, id: clienteId },
    })
  }
  // Función para cerrar el editor
  const handleCloseEdit = async (wasSuccessful = false) => {
    setEditDialog({ open: false, cliente: null })

    if (wasSuccessful) {
      // Solo recargar y mostrar notificación si fue exitoso
      const mountedRef = { current: true }
      await fetchClientes(mountedRef)

      // Limpiar selección
      setSelected([])

      // Mostrar notificación de éxito
      setNotification({
        open: true,
        message: 'Cliente actualizado exitosamente',
        severity: 'success'
      })
    }
  }

  // Función para abrir dialog de confirmación
  const handleDeleteCliente = (cliente) => {
    setConfirmDialog({
      open: true,
      cliente: cliente,
      multiple: false,
    })
  }

  // Función para abrir dialog de confirmación para múltiples clientes
  const handleDeleteSelected = () => {
    if (!selected || selected.length === 0) return

    setConfirmDialog({
      open: true,
      cliente: null,
      multiple: true,
    })
  }

  // Función para confirmar el borrado (1 o varios)
  const handleConfirmDelete = async () => {
    const { multiple, cliente } = confirmDialog

    // Cerrar dialog
    setConfirmDialog({ open: false, cliente: null, multiple: false })

    // Determinar qué IDs borrar
    const idsToDelete = multiple
      ? [
          ...new Set(
            (selected || [])
              .map((selectedId) => {
                const row = clientes.find((c) => c.id === selectedId)
                return row?.id_cliente ?? row?.cliente_id ?? selectedId
              })
              .filter(Boolean)
          ),
        ]
      : cliente
        ? [cliente.id_cliente ?? cliente.cliente_id ?? cliente.id]
        : []

    if (idsToDelete.length === 0) return

    try {
      if (multiple) {
        const res = await apiFetch("/api/clientes/bulk-delete", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: idsToDelete }),
        });

        if (!res.ok) throw new Error('Error al borrar los consultas seleccionadas');
      } else {
        const results = await Promise.all(
          idsToDelete.map((id) =>
            apiFetch(`/api/clientes/${id}`, {
              method: 'DELETE',
              credentials: 'include',
            })
          )
        )
  
        const allOk = results.every((res) => res.ok)
        if (!allOk) {
          throw new Error('Error al borrar uno o más clientes')
        }
      }

      // Recargar la lista
      const mountedRef = { current: true }
      await fetchClientes(mountedRef)

      // Limpiar selección
      setSelected([])

      // Mostrar notificación de éxito
      setNotification({
        open: true,
        message: multiple
          ? `Se borraron ${idsToDelete.length} clientes exitosamente`
          : `Cliente "${cliente?.nombre}" borrado exitosamente`,
        severity: 'success'
      })

    } catch (error) {

      // Mostrar notificación de error
      setNotification({
        open: true,
        message: `Error al borrar cliente(s): ${error.message}`,
        severity: 'error'
      })
    }
  }

  // Función para cancelar el borrado
  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, cliente: null, multiple: false })
  }

  // Función para cerrar notificaciones
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }))
  }

  const selectedCliente = getSelectedCliente()

  const handleOpenImportCsv = () => {
    // Pausar refetches del socket mientras se realiza la importación y resolución de duplicados
    pauseSocketRefetch.current = true
    setImportCsvOpen(true)
  }

  const handleCloseImportCsv = async (result) => {
    // REACTIVAR SIEMPRE el socket en todos los cierres (cancelación, errores, finales)
    pauseSocketRefetch.current = false
    setImportCsvOpen(false)

    // Resetear a la primera página para refrescar la lista
    setPage(0)
    setSelected([])

    const mountedRef = { current: true }
    await fetchClientes(mountedRef)

    if (result) {
      const clientesCreados = result.clientes_creados ?? 0;
      const consultasCreadas = result.consultas_creadas ?? 0;
      const consultasAgregadas = result.consultas_agregadas ?? 0;
      const repetidas = result.repetidas ?? 0;
      const conflictos = result.conflictos ?? 0;
      const errores = result.errores ?? 0;

      setNotification({
        open: true,
        message:
          `Importación completada: ${clientesCreados} clientes creados, ` +
          `${consultasCreadas} consultas creadas (${consultasAgregadas} agregadas), ` +
          `${repetidas} repetidas, ${conflictos} conflictos, ${errores} errores.`,
        severity: 'success',
      })
    }
  }


  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden', // Evitar desbordes
      px: isFullScreen ? 2 : 5,
      pt: isFullScreen ? 3 : 4,
      transition: 'padding 0.3s ease'
    }}>
      <ConsultasNoAsignHead
        canAssign={selected.length > 0}
        sortOrder={sortOrder}
        selectedCount={selected.length}
        selectedCliente={selectedCliente}
        onChangeSort={handleChangeSort}
        onClose={onClose}
        onOpenAsignacion={handleOpenAsignacion}
        onOpenClosed={onOpenClosed}
        onOpenClientes={onOpenClientes}
        onEditCliente={handleEditCliente}
        onDeleteCliente={handleDeleteCliente}
        onDeleteSelected={handleDeleteSelected}
        onOpenImportCsv={handleOpenImportCsv}
        isFullScreen={isFullScreen}
        onToggleFullScreen={onToggleFullScreen}
      />

      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <ConsultasNoAsignTable
          clientes={clientes}
          selectedIds={selected}
          highlightConsultaId={activeHighlightId}
          onToggleAll={toggleSelectAll}
          onToggleRow={toggleRow}
          // Props de paginación
          count={total}
          page={page}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          customHeight={isFullScreen ? "flex" : undefined}
        />
      )}

      {/* Reutilizamos el modal CrearAsignacion: abrimos el modal y le pasamos los consultas seleccionadas mediante evento */}
      {openAsignacion && (
        <CrearAsignacion
          openControlled={openAsignacion}
          onCloseControlled={handleCloseAsignacion}
          initialSelectedClients={selected}
          showTriggerButton={false}
        />
      )}

      {/* Modal de Edición de Cliente */}
      <EditarClienteContainer
        openControlled={editDialog.open}
        onCloseControlled={handleCloseEdit}
        cliente={editDialog.cliente}
      />

      <ImportClientesCsvDialog
        open={importCsvOpen}
        onClose={handleCloseImportCsv}
        onImported={handleCloseImportCsv}
      />

      {/* Dialog de Confirmación para Borrar */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={confirmDialog.multiple ? "Borrar consultas" : "Borrar consulta"}
        message={
          confirmDialog.multiple
            ? `¿Estás seguro de que deseas borrar los ${selected.length} consultas seleccionadas? Esta acción no se puede deshacer.`
            : `¿Estás seguro de que deseas borrar el cliente "${confirmDialog.cliente?.nombre}"?`
        }
        confirmText="Borrar"
        cancelText="Cancelar"
        severity="error"
      />
      {/* Notificaciones */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </Box>
  )
}
